import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";
import {
  Eye,
  EyeOff,
  User,
  ArrowRight,
  ArrowLeft,
  Mail,
  Shield,
  ChevronLeft,
  ChevronRight,
  KeyRound,
  Phone,
  X,
} from "lucide-react";
import InputField from "./components/InputComponent/InputField";
import axios from "axios";
import "./Login.scss";
import { getPostLoginRedirect } from "./utils/selfDiscovery";

export default function SignIn() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("login");
  const [currentSlide, setCurrentSlide] = useState(0);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    email: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [otp, setOtp] = useState("");
  // ── Forgot password wizard step: 'email' -> 'otp' -> 'reset' ──
  const [forgotStep, setForgotStep] = useState("email");
  // ── Resend OTP countdown (10 minutes) ──
  const OTP_RESEND_SECONDS = 10 * 60;
  const [otpTimer, setOtpTimer] = useState(0);

  useEffect(() => {
    if (otpTimer <= 0) return undefined;
    const id = setTimeout(() => setOtpTimer((prev) => prev - 1), 1000);
    return () => clearTimeout(id);
  }, [otpTimer]);

  const formatOtpTimer = (secs) => {
    const m = Math.floor(secs / 60)
      .toString()
      .padStart(2, "0");
    const s = (secs % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  // ── Google Sign-Up completion (new users must supply phone + password) ──
  const [googleUserData, setGoogleUserData] = useState(null);
  const [showGoogleCompleteModal, setShowGoogleCompleteModal] = useState(false);
  const [googleCompleteData, setGoogleCompleteData] = useState({
    mobileNumber: "",
    password: "",
    confirmPassword: "",
  });
  const [showGooglePassword, setShowGooglePassword] = useState(false);
  const [showGoogleConfirmPassword, setShowGoogleConfirmPassword] =
    useState(false);

  const handleGoogleCompleteInputChange = (field, value) =>
    setGoogleCompleteData((prev) => ({ ...prev, [field]: value }));
  const [registerData, setRegisterData] = useState({
    userName: "",
    email: "",
    mobileNumber: "",
    password: "",
    userType: "",
  });

  const handleRegisterInputChange = (field, value) =>
    setRegisterData((prev) => ({ ...prev, [field]: value }));

  const switchToRegister = () => {
    clearMessages();
    setActiveTab("register");
  };
  const switchToForgotPassword = () => {
    clearMessages();
    setOtp("");
    setForgotStep("email");
    setOtpTimer(0);
    setActiveTab("forgot");
  };
  const switchToLogin = () => {
    clearMessages();
    setActiveTab("login");
  };

  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showRegisterConfirmPassword, setShowRegisterConfirmPassword] =
    useState(false);

  // ─── Google Sign-In Setup ────────────────────────────────────────────────────
  useEffect(() => {
    // Google's ID token (JWT) carries the user's email in its payload, so we
    // decode it client-side to pass along when completing registration.
    const decodeGoogleEmail = (credential) => {
      try {
        const payload = credential.split(".")[1];
        const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
        const decoded = JSON.parse(atob(normalized));
        return decoded.email || "";
      } catch {
        return "";
      }
    };

    // ── define callback inside effect so it's stable ──
    const handleGoogleCallback = async ({ credential: tokenId }) => {
      clearMessages();
      setLoading(true);
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/auth/google`,
          { tokenId },
          { withCredentials: true },
        );
        if (response.status === 200) {
          const { userId, userName, jwtToken, newUser } = response.data.data;

          if (newUser) {
            // New Google account: hold onto the auth details and ask the
            // user for a phone number + password before we finish login.
            setGoogleUserData({
              userId,
              userName,
              jwtToken,
              email: decodeGoogleEmail(tokenId),
            });
            setGoogleCompleteData({
              mobileNumber: "",
              password: "",
              confirmPassword: "",
            });
            setShowGoogleCompleteModal(true);
            return;
          }

          login({ userId, userName }, jwtToken);
          sessionStorage.setItem("jwtToken", jwtToken);
          sessionStorage.setItem("username", userName);
          setSuccess("Login successful! Redirecting...");
          setTimeout(() => navigate(getPostLoginRedirect()), 1000);
        }
      } catch (err) {
        setError(
          err.response?.data?.message ||
            "Google login failed. Please try again.",
        );
      } finally {
        setLoading(false);
      }
    };

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => {
      window.google?.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        callback: handleGoogleCallback,
      });
      window.google?.accounts.id.renderButton(
        document.getElementById("google-signin-btn"),
        {
          theme: "outline",
          size: "large",
          width: "100%",
          text: "signin_with",
          shape: "rectangular",
        },
      );
    };
    document.body.appendChild(script);
    return () => document.body.removeChild(script);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRegister = async (event) => {
    event.preventDefault();
    clearMessages();
    const {
      userName,
      email,
      mobileNumber,
      password,
      confirmPassword,
      userType,
    } = registerData;
    if (!userName || !email || !mobileNumber || !password) {
      setError("All fields are required.");
      return;
    }
    if (!passwordValidation(password)) {
      setError(
        "Password must be at least 8 characters and include 1 uppercase letter, 1 symbol, and 1 number.",
      );
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/register`,
        { userName, email, mobileNumber, password, userType },
      );
      if (response.status === 200) {
        setSuccess("Registration successful! Please sign in.");
        setTimeout(() => switchToLogin(), 2000);
      }
    } catch (error) {
      setError(error.response?.data?.message || "Registration failed.");
    } finally {
      setLoading(false);
    }
  };

  const selfDiscoverySlides = [
    {
      title: "Begin Your Journey Within",
      content: "Welcome to your personal growth companion.",
      subtitle: "Unlock your potential and understand what makes you, you.",
      features: [
        "🧘‍♀️ Discover guided mindfulness exercises",
        "📝 Reflect with daily journaling prompts",
        "🎯 Set and track meaningful personal goals",
      ],
      footer: "Your path to self-awareness starts now.",
      image: "https://cdn-icons-png.flaticon.com/512/3048/3048325.png",
    },
    {
      title: "Uncover Your Strengths",
      content: "What are your unique superpowers?",
      subtitle:
        "Our guided assessments help you identify your core strengths and passions.",
      features: [
        "💡 Take insightful personality quizzes",
        "💪 Identify your top character strengths",
        "❤️ Align your life with your core values",
      ],
      footer: "Live a more authentic and fulfilling life.",
      image: "https://cdn-icons-png.flaticon.com/512/4345/4345728.png",
    },
    {
      title: "Cultivate Positive Habits",
      content: "Small, consistent steps lead to big changes.",
      subtitle:
        "Create lasting, positive change with our intuitive habit tracker.",
      features: [
        "✅ Create and customize your daily habits",
        "📈 Visualize your progress with beautiful charts",
        "🎉 Celebrate your milestones and stay motivated",
      ],
      footer: "Transform your life, one habit at a time.",
      image: "https://cdn-icons-png.flaticon.com/512/2589/2589175.png",
    },
  ];

  const currentSlides = selfDiscoverySlides;

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % currentSlides.length);
    }, 7000);
    return () => clearInterval(interval);
  }, [currentSlides.length]);

  const handleInputChange = (field, value) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    clearMessages();
    const { username, password } = formData;
    if (!username || !password) {
      setError("Username and Password are required");
      return;
    }
    setLoading(true);
    const token = btoa(`${username}:${password}`);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/userLogin`,
        {
          method: "POST",
          headers: {
            Authorization: `Basic ${token}`,
            "X-ProductID": process.env.REACT_APP_PRODUCT_ID,
          },
          credentials: "include",
        },
      );
      const responseBody = await response.text();
      if (!response.ok) throw new Error(responseBody || "Login failed");
      const data = JSON.parse(responseBody);
      const jwtToken = response.headers.get("Authorization");
      if (data.status === "OK" && jwtToken) {
        login(data, jwtToken);
        sessionStorage.setItem("jwtToken", jwtToken);
        sessionStorage.setItem("username", username);
        setSuccess("Login successful! Redirecting...");
        setTimeout(() => navigate(getPostLoginRedirect()), 1000);
      } else {
        setError(data.message || "Invalid credentials or unexpected response.");
      }
    } catch (error) {
      setError(error.message || "Login failed, please try again later");
    } finally {
      setLoading(false);
    }
  };

  // Step 1 — request an OTP for the account email
  const handleSendOtp = async (event) => {
    event.preventDefault();
    clearMessages();
    const { email } = formData;
    if (!email) {
      setError("Email is required.");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/send-otp`,
        { email },
      );
      if (response.status === 200) {
        setSuccess(response.data?.message || "OTP sent to your email.");
        setForgotStep("otp");
        setOtpTimer(OTP_RESEND_SECONDS);
      }
    } catch (error) {
      setError(error.response?.data?.message || "Failed to send OTP.");
    } finally {
      setLoading(false);
    }
  };

  // Step 2 — verify the OTP the user received
  const handleVerifyOtp = async (event) => {
    event.preventDefault();
    clearMessages();
    const { email } = formData;
    if (!otp) {
      setError("OTP is required.");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/verify-otp`,
        { email, otp },
      );
      if (response.status === 200) {
        setSuccess(response.data?.message || "OTP verified.");
        setForgotStep("reset");
      }
    } catch (error) {
      setError(
        error.response?.data?.message || "Invalid OTP. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  // Step 3 — set the new password now that the OTP has been verified
  const handleResetPassword = async (event) => {
    event.preventDefault();
    clearMessages();
    const { email, newPassword, confirmPassword } = formData;
    if (!newPassword || !confirmPassword) {
      setError("All fields are required.");
      return;
    }
    if (!passwordValidation(newPassword)) {
      setError(
        "Password must be at least 8 characters and include 1 uppercase letter, 1 symbol, and 1 number.",
      );
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/reset-password`,
        { email, password: newPassword },
      );
      if (response.status === 200) {
        setSuccess(
          response.data?.message ||
            "Password changed successfully! Please login.",
        );
        setTimeout(() => switchToLogin(), 2000);
      }
    } catch (error) {
      setError(error.response?.data?.message || "Failed to reset password.");
    } finally {
      setLoading(false);
    }
  };

  // Finishes account setup for a brand-new Google sign-up (phone + password)
  const handleCompleteGoogleRegistration = async (event) => {
    event.preventDefault();
    clearMessages();
    const { mobileNumber, password, confirmPassword } = googleCompleteData;
    if (!mobileNumber || !password || !confirmPassword) {
      setError("All fields are required.");
      return;
    }
    if (!passwordValidation(password)) {
      setError(
        "Password must be at least 8 characters and include 1 uppercase letter, 1 symbol, and 1 number.",
      );
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/auth/complete-google-registration`,
        {
          email: googleUserData?.email,
          mobileNumber: Number(mobileNumber),
          password,
        },
      );
      if (response.status === 200) {
        const { userId, userName, jwtToken } = googleUserData;
        login({ userId, userName }, jwtToken);
        sessionStorage.setItem("jwtToken", jwtToken);
        sessionStorage.setItem("username", userName);
        setShowGoogleCompleteModal(false);
        setGoogleUserData(null);
        setSuccess("Account created! Redirecting...");
        setTimeout(() => navigate(getPostLoginRedirect()), 1000);
      }
    } catch (error) {
      setError(
        error.response?.data?.message || "Failed to complete registration.",
      );
    } finally {
      setLoading(false);
    }
  };

  const nextSlide = () =>
    setCurrentSlide((prev) => (prev + 1) % currentSlides.length);
  const prevSlide = () =>
    setCurrentSlide(
      (prev) => (prev - 1 + currentSlides.length) % currentSlides.length,
    );

  const renderSlideContent = (slide) => (
    <div className="text-center text-white">
      {slide.image && (
        <img
          src={slide.image}
          alt="Feature"
          className="w-24 h-24 mx-auto opacity-90 mb-6"
        />
      )}
      <h2 className="text-4xl font-extrabold text-white mb-4 tracking-wide drop-shadow-[0_0_12px_rgba(255,255,255,0.6)]">
        {slide.title}
      </h2>
      <p className="text-xl font-semibold text-slate-100 mb-6 drop-shadow-[0_0_8px_rgba(255,255,255,0.3)]">
        {slide.content}
      </p>
      {slide.subtitle && (
        <p className="text-lg text-slate-200 font-medium mb-6 drop-shadow-[0_0_8px_rgba(255,255,255,0.2)]">
          {slide.subtitle}
        </p>
      )}
      {slide.features && (
        <div className="space-y-3 mb-6">
          {slide.features.map((feature, index) => (
            <p
              key={index}
              className="text-slate-100 font-semibold drop-shadow-[0_0_6px_rgba(255,255,255,0.2)] text-left"
            >
              {feature}
            </p>
          ))}
        </div>
      )}
      {slide.footer && (
        <p className="text-lg text-slate-200 font-semibold drop-shadow-[0_0_6px_rgba(255,255,255,0.2)]">
          {slide.footer}
        </p>
      )}
    </div>
  );

  const getRotation = () => {
    switch (activeTab) {
      case "login":
        return "rotate-y-0";
      case "register":
      case "forgot":
        return "rotate-y-180";
      default:
        return "rotate-y-0";
    }
  };

  const passwordValidation = (password) => {
    return (
      /[A-Z]/.test(password) &&
      /[!@#$%^&*(),.?":{}|<>]/.test(password) &&
      /\d/.test(password) &&
      password.length >= 8
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 relative bg-black">
      <div className="relative max-w-4xl w-full bg-white/10 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 z-10">
        <div
          className={`relative z-10 w-full perspective-1000 overflow-hidden rounded-3xl transition-all duration-500 ${activeTab === "register" ? "h-[950px]" : "h-[700px]"}`}
        >
          <div
            className={`relative w-full h-full transition-transform duration-700 transform-style-preserve-3d ${getRotation()}`}
          >
            {/* LOGIN FACE */}
            <div className="absolute inset-0 backface-hidden">
              <div className="flex flex-col lg:flex-row w-full h-full">
                {/* Left panel - slides */}
                <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-900/70 via-indigo-900/50 to-blue-900/40 backdrop-blur-xl rounded-l-3xl p-8 flex-col justify-center relative border-r border-white/10 shadow-xl">
                  <div className="max-w-md mx-auto w-full">
                    {renderSlideContent(currentSlides[currentSlide])}
                  </div>
                  <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex items-center space-x-4">
                    <button
                      onClick={prevSlide}
                      className="p-2 rounded-full bg-white/60 backdrop-blur-md hover:bg-white/80 shadow-lg transition-all duration-200"
                    >
                      <ChevronLeft className="w-5 h-5 text-slate-600" />
                    </button>
                    <div className="flex space-x-2">
                      {currentSlides.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentSlide(index)}
                          className={`w-3 h-3 rounded-full transition-all duration-200 ${index === currentSlide ? "bg-indigo-500 shadow-lg" : "bg-slate-300/60 hover:bg-slate-400/60"}`}
                        />
                      ))}
                    </div>
                    <button
                      onClick={nextSlide}
                      className="p-2 rounded-full bg-white/60 backdrop-blur-md hover:bg-white/80 shadow-lg transition-all duration-200"
                    >
                      <ChevronRight className="w-5 h-5 text-slate-600" />
                    </button>
                  </div>
                </div>

                {/* Right panel - login form */}
                <div className="w-full lg:w-1/2 p-8 flex flex-col justify-center bg-white/90 backdrop-blur-xl rounded-3xl lg:rounded-r-3xl lg:rounded-l-none shadow-2xl">
                  <div className="relative z-10 max-w-md mx-auto w-full">
                    <div className="text-center mb-8">
                      <h2 className="text-3xl font-bold text-slate-800 mb-2">
                        Welcome Back
                      </h2>
                      <p className="text-slate-600">
                        Enter your credentials to continue
                      </p>
                    </div>
                    {error && (
                      <div className="bg-red-50/80 border border-red-200/60 text-red-700 p-3 rounded-xl mb-4 text-center">
                        {error}
                      </div>
                    )}
                    {success && (
                      <div className="bg-emerald-50/80 border border-emerald-200/60 text-emerald-700 p-3 rounded-xl mb-4 text-center">
                        {success}
                      </div>
                    )}

                    <form className="space-y-6" onSubmit={handleSubmit}>
                      <InputField
                        label="Username"
                        type="text"
                        value={formData.username}
                        onChange={(e) =>
                          handleInputChange("username", e.target.value)
                        }
                        placeholder="Enter your username"
                        icon={User}
                      />
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">
                          Password
                        </label>
                        <div className="relative">
                          <input
                            type={showPassword ? "text" : "password"}
                            value={formData.password}
                            onChange={(e) =>
                              handleInputChange("password", e.target.value)
                            }
                            className="w-full pl-2 pr-12 py-3 bg-white/70 border border-slate-200/60 rounded-xl"
                            placeholder="Enter your password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                          >
                            {showPassword ? (
                              <EyeOff className="w-5 h-5" />
                            ) : (
                              <Eye className="w-5 h-5" />
                            )}
                          </button>
                        </div>
                      </div>

                      <button
                        type="submit"
                        disabled={loading}
                        className={`w-full bg-gradient-to-r from-indigo-500 to-blue-600 text-white py-3 rounded-xl font-medium flex items-center justify-center ${loading ? "opacity-70" : ""}`}
                      >
                        {loading ? "Signing in..." : "Sign In"}
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </button>

                      {/* ─── Google Sign-In Button ─────────────────────────── */}
                      <div className="relative flex items-center my-1">
                        <div className="flex-grow border-t border-slate-200" />
                        <span className="mx-3 text-sm text-slate-400 font-medium">
                          or
                        </span>
                        <div className="flex-grow border-t border-slate-200" />
                      </div>
                      <div
                        id="google-signin-btn"
                        className="w-full flex justify-center"
                      />
                      {/* ──────────────────────────────────────────────────── */}

                      <div className="text-center mt-2">
                        <button
                          onClick={switchToRegister}
                          type="button"
                          className="text-indigo-600 hover:text-indigo-800 text-lg font-medium"
                        >
                          New user? Register here
                        </button>
                      </div>
                      <div className="text-center mt-2">
                        <button
                          onClick={switchToForgotPassword}
                          type="button"
                          className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                        >
                          Forgot your password?
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>

            {/* BACK FACE */}
            <div className="absolute inset-0 backface-hidden rotate-y-180">
              {/* Register View */}
              <div
                className={`w-full h-full absolute transition-opacity duration-300 ${activeTab === "register" ? "opacity-100" : "opacity-0 pointer-events-none"}`}
              >
                <div className="flex flex-col lg:flex-row w-full h-full">
                  <div className="w-full lg:w-1/2 p-8 flex flex-col justify-center bg-white/90 backdrop-blur-xl rounded-3xl lg:rounded-l-3xl lg:rounded-r-none shadow-2xl">
                    <div className="relative z-10 max-w-md mx-auto w-full">
                      <div className="text-center mb-8">
                        <h2 className="text-3xl font-bold text-slate-800 mb-2">
                          Sign Up
                        </h2>
                        <p className="text-slate-600">
                          Create your account to start your journey!
                        </p>
                      </div>
                      {error && (
                        <div className="bg-red-50/80 border border-red-200/60 text-red-700 p-3 rounded-xl mb-4 text-center">
                          {error}
                        </div>
                      )}
                      {success && (
                        <div className="bg-emerald-50/80 border border-emerald-200/60 text-emerald-700 p-3 rounded-xl mb-4 text-center">
                          {success}
                        </div>
                      )}
                      <form className="space-y-4" onSubmit={handleRegister}>
                        <InputField
                          label="User Name"
                          type="text"
                          value={registerData.userName}
                          onChange={(e) =>
                            handleRegisterInputChange(
                              "userName",
                              e.target.value,
                            )
                          }
                          icon={User}
                        />
                        <InputField
                          label="Email"
                          type="email"
                          value={registerData.email}
                          onChange={(e) =>
                            handleRegisterInputChange("email", e.target.value)
                          }
                          icon={Mail}
                        />
                        <InputField
                          label="Mobile Number"
                          type="number"
                          value={registerData.mobileNumber}
                          onChange={(e) =>
                            handleRegisterInputChange(
                              "mobileNumber",
                              e.target.value,
                            )
                          }
                          icon={User}
                        />
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Password
                        </label>
                        <div className="relative mb-4">
                          <input
                            type={showRegisterPassword ? "text" : "password"}
                            value={registerData.password}
                            onChange={(e) =>
                              handleRegisterInputChange(
                                "password",
                                e.target.value,
                              )
                            }
                            className="w-full pl-2 pr-12 py-3 bg-white/70 border border-slate-200/60 rounded-xl"
                            placeholder="Enter your password"
                          />
                          <button
                            type="button"
                            onClick={() =>
                              setShowRegisterPassword(!showRegisterPassword)
                            }
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                          >
                            {showRegisterPassword ? (
                              <EyeOff className="w-5 h-5" />
                            ) : (
                              <Eye className="w-5 h-5" />
                            )}
                          </button>
                        </div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Confirm Password
                        </label>
                        <div className="relative">
                          <input
                            type={
                              showRegisterConfirmPassword ? "text" : "password"
                            }
                            value={registerData.confirmPassword}
                            onChange={(e) =>
                              handleRegisterInputChange(
                                "confirmPassword",
                                e.target.value,
                              )
                            }
                            className="w-full pl-2 pr-12 py-3 bg-white/70 border border-slate-200/60 rounded-xl"
                            placeholder="Confirm your password"
                          />
                          <button
                            type="button"
                            onClick={() =>
                              setShowRegisterConfirmPassword(
                                !showRegisterConfirmPassword,
                              )
                            }
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                          >
                            {showRegisterConfirmPassword ? (
                              <EyeOff className="w-5 h-5" />
                            ) : (
                              <Eye className="w-5 h-5" />
                            )}
                          </button>
                        </div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          User Type
                        </label>
                        <select
                          value={registerData.userType}
                          onChange={(e) =>
                            handleRegisterInputChange(
                              "userType",
                              e.target.value,
                            )
                          }
                          className="w-full bg-white/70 border border-slate-200/60 rounded-xl py-3 px-2"
                        >
                          <option value="">Select User Type</option>
                          <option value="ACCOUNTANT">Accountant</option>
                          <option value="BUSINESS_OWNER">Business Owner</option>
                          <option value="DEVELOPER">Developer</option>
                          <option value="HR">HR</option>
                          <option value="SALES">Sales</option>
                        </select>
                        <button
                          type="submit"
                          disabled={loading}
                          className={`w-full bg-gradient-to-r from-indigo-500 to-blue-600 text-white py-3 rounded-xl font-medium flex items-center justify-center transition-opacity duration-200 ${loading ? "opacity-70" : ""}`}
                        >
                          {loading ? "Registering..." : "Register"}
                          <ArrowRight className="ml-2 w-5 h-5" />
                        </button>
                        <div className="text-center">
                          <button
                            type="button"
                            onClick={switchToLogin}
                            className="text-indigo-600 hover:text-indigo-800 text-sm font-medium mt-2 flex items-center justify-center w-full"
                          >
                            <ArrowLeft className="w-4 h-4 mr-1" /> Already a
                            user? Sign In
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                  <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-indigo-900 via-purple-900 to-blue-800 backdrop-blur-xl rounded-r-3xl p-10 flex-col justify-center shadow-2xl">
                    <div className="max-w-md mx-auto w-full text-center space-y-8">
                      <div className="w-24 h-24 bg-white/10 rounded-full flex items-center justify-center mx-auto shadow-xl ring-2 ring-white/20">
                        <User className="w-12 h-12 text-indigo-300" />
                      </div>
                      <h1 className="text-5xl font-extrabold text-white tracking-tight">
                        Begin Your{" "}
                        <span className="text-indigo-300">Self-Discovery</span>
                      </h1>
                      <p className="text-xl font-semibold text-indigo-100 leading-relaxed">
                        Unlock insights, explore your personality, and grow with
                        guided tests and recommendations.
                      </p>
                      <div className="flex items-center justify-center space-x-2 text-base font-semibold text-indigo-200 mt-6">
                        <div className="w-3 h-3 rounded-full bg-indigo-400"></div>
                        <span>
                          Step <span className="text-white">1</span> of Your
                          Journey
                        </span>
                        <div className="w-3 h-3 rounded-full bg-indigo-400"></div>
                      </div>
                      <blockquote className="italic text-lg font-medium text-indigo-100 mt-4 border-l-4 border-indigo-400 pl-4">
                        "The first step toward change is{" "}
                        <span className="font-bold text-white">awareness</span>
                        ."
                      </blockquote>
                    </div>
                  </div>
                </div>
              </div>

              {/* Forgot Password View */}
              <div
                className={`w-full h-full absolute transition-opacity duration-300 ${activeTab === "forgot" ? "opacity-100" : "opacity-0 pointer-events-none"}`}
              >
                <div className="flex flex-col lg:flex-row w-full h-full">
                  <div className="w-full lg:w-1/2 p-8 flex flex-col justify-center bg-white/90 backdrop-blur-xl rounded-3xl lg:rounded-l-3xl lg:rounded-r-none shadow-2xl">
                    <div className="relative z-10 max-w-md mx-auto w-full">
                      <div className="text-center mb-8">
                        <h2 className="text-3xl font-bold text-slate-800 mb-2">
                          Reset Password
                        </h2>
                        <p className="text-slate-600">
                          Enter your details to recover your account
                        </p>
                      </div>
                      {error && (
                        <div className="bg-red-50/80 border border-red-200/60 text-red-700 p-3 rounded-xl mb-4 text-center">
                          {error}
                        </div>
                      )}
                      {success && (
                        <div className="bg-emerald-50/80 border border-emerald-200/60 text-emerald-700 p-3 rounded-xl mb-4 text-center">
                          {success}
                        </div>
                      )}
                      {/* Step indicator */}
                      <div className="flex items-center justify-center mb-6 space-x-2">
                        {["email", "otp", "reset"].map((step, idx) => (
                          <React.Fragment key={step}>
                            <div
                              className={`w-2.5 h-2.5 rounded-full transition-all duration-200 ${
                                forgotStep === step
                                  ? "bg-indigo-500 scale-125"
                                  : ["email", "otp", "reset"].indexOf(
                                        forgotStep,
                                      ) > idx
                                    ? "bg-indigo-300"
                                    : "bg-slate-300/60"
                              }`}
                            />
                            {idx < 2 && (
                              <div className="w-8 h-px bg-slate-300/60" />
                            )}
                          </React.Fragment>
                        ))}
                      </div>

                      {/* Step 1: Enter email, request OTP */}
                      {forgotStep === "email" && (
                        <form className="space-y-4" onSubmit={handleSendOtp}>
                          <InputField
                            label="Email Address"
                            type="email"
                            value={formData.email}
                            onChange={(e) =>
                              handleInputChange("email", e.target.value)
                            }
                            placeholder="Enter your email"
                            icon={Mail}
                          />
                          <button
                            type="submit"
                            disabled={loading}
                            className={`w-full bg-gradient-to-r from-indigo-500 to-blue-600 text-white py-3 rounded-xl font-medium flex items-center justify-center ${loading ? "opacity-70" : ""}`}
                          >
                            {loading ? "Sending OTP..." : "Send OTP"}
                            <ArrowRight className="ml-2 w-5 h-5" />
                          </button>
                          <div className="text-center">
                            <button
                              type="button"
                              onClick={switchToLogin}
                              className="text-indigo-600 hover:text-indigo-800 text-sm font-medium flex items-center justify-center w-full"
                            >
                              <ArrowLeft className="w-4 h-4 mr-1" /> Back to
                              Sign In
                            </button>
                          </div>
                        </form>
                      )}

                      {/* Step 2: Enter and verify OTP */}
                      {forgotStep === "otp" && (
                        <form className="space-y-4" onSubmit={handleVerifyOtp}>
                          <p className="text-sm text-slate-600 text-center -mt-2 mb-2">
                            We sent a code to{" "}
                            <span className="font-medium text-slate-800">
                              {formData.email}
                            </span>
                          </p>
                          <InputField
                            label="OTP"
                            type="text"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value)}
                            placeholder="Enter the OTP"
                            icon={KeyRound}
                          />
                          <button
                            type="submit"
                            disabled={loading}
                            className={`w-full bg-gradient-to-r from-indigo-500 to-blue-600 text-white py-3 rounded-xl font-medium flex items-center justify-center ${loading ? "opacity-70" : ""}`}
                          >
                            {loading ? "Verifying..." : "Verify OTP"}
                            <ArrowRight className="ml-2 w-5 h-5" />
                          </button>
                          <div className="flex items-center justify-between text-sm">
                            <button
                              type="button"
                              onClick={() => {
                                setForgotStep("email");
                                setOtpTimer(0);
                              }}
                              className="text-indigo-600 hover:text-indigo-800 font-medium flex items-center"
                            >
                              <ArrowLeft className="w-4 h-4 mr-1" /> Change
                              email
                            </button>
                            <button
                              type="button"
                              disabled={loading || otpTimer > 0}
                              onClick={handleSendOtp}
                              className={`font-medium ${
                                loading || otpTimer > 0
                                  ? "text-slate-400 cursor-not-allowed"
                                  : "text-indigo-600 hover:text-indigo-800"
                              }`}
                            >
                              {otpTimer > 0
                                ? `Resend OTP in ${formatOtpTimer(otpTimer)}`
                                : "Resend OTP"}
                            </button>
                          </div>
                        </form>
                      )}

                      {/* Step 3: Set new password */}
                      {forgotStep === "reset" && (
                        <form
                          className="space-y-4"
                          onSubmit={handleResetPassword}
                        >
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-700">
                              New Password
                            </label>
                            <div className="relative">
                              <input
                                type={showNewPassword ? "text" : "password"}
                                value={formData.newPassword}
                                onChange={(e) =>
                                  handleInputChange(
                                    "newPassword",
                                    e.target.value,
                                  )
                                }
                                className="w-full pl-4 pr-12 py-3 bg-white/70 border border-slate-200/60 rounded-xl"
                                placeholder="Enter new password"
                              />
                              <button
                                type="button"
                                onClick={() =>
                                  setShowNewPassword(!showNewPassword)
                                }
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                              >
                                {showNewPassword ? (
                                  <EyeOff className="w-5 h-5" />
                                ) : (
                                  <Eye className="w-5 h-5" />
                                )}
                              </button>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-700">
                              Confirm Password
                            </label>
                            <div className="relative">
                              <input
                                type={showConfirmPassword ? "text" : "password"}
                                value={formData.confirmPassword}
                                onChange={(e) =>
                                  handleInputChange(
                                    "confirmPassword",
                                    e.target.value,
                                  )
                                }
                                className="w-full pl-4 pr-12 py-3 bg-white/70 border border-slate-200/60 rounded-xl"
                                placeholder="Confirm new password"
                              />
                              <button
                                type="button"
                                onClick={() =>
                                  setShowConfirmPassword(!showConfirmPassword)
                                }
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                              >
                                {showConfirmPassword ? (
                                  <EyeOff className="w-5 h-5" />
                                ) : (
                                  <Eye className="w-5 h-5" />
                                )}
                              </button>
                            </div>
                          </div>
                          <button
                            type="submit"
                            disabled={loading}
                            className={`w-full bg-gradient-to-r from-indigo-500 to-blue-600 text-white py-3 rounded-xl font-medium flex items-center justify-center ${loading ? "opacity-70" : ""}`}
                          >
                            {loading ? "Resetting..." : "Reset Password"}
                            <ArrowRight className="ml-2 w-5 h-5" />
                          </button>
                          <div className="text-center">
                            <button
                              type="button"
                              onClick={switchToLogin}
                              className="text-indigo-600 hover:text-indigo-800 text-sm font-medium flex items-center justify-center w-full"
                            >
                              <ArrowLeft className="w-4 h-4 mr-1" /> Back to
                              Sign In
                            </button>
                          </div>
                        </form>
                      )}
                    </div>
                  </div>
                  <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-900/70 via-indigo-900/50 to-blue-900/40 backdrop-blur-xl rounded-r-3xl p-8 flex-col justify-center">
                    <div className="max-w-md mx-auto w-full text-center">
                      <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Shield className="w-10 h-10 text-indigo-600" />
                      </div>
                      <h1 className="text-4xl text-white font-bold mb-4">
                        Secure Recovery
                      </h1>
                      <p className="text-lg text-white">
                        We'll help you regain access safely.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Complete Google Sign-Up Modal (shown when newUser === true) ──── */}
      {showGoogleCompleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl p-8">
            <button
              type="button"
              onClick={() => setShowGoogleCompleteModal(false)}
              className="absolute right-4 top-4 text-slate-400 hover:text-slate-600"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-slate-800 mb-2">
                Almost there!
              </h2>
              <p className="text-slate-600">
                Add a phone number and password to finish setting up your
                account
                {googleUserData?.email ? ` for ${googleUserData.email}` : ""}.
              </p>
            </div>
            {error && (
              <div className="bg-red-50/80 border border-red-200/60 text-red-700 p-3 rounded-xl mb-4 text-center">
                {error}
              </div>
            )}
            <form
              className="space-y-4"
              onSubmit={handleCompleteGoogleRegistration}
            >
              <InputField
                label="Mobile Number"
                type="number"
                value={googleCompleteData.mobileNumber}
                onChange={(e) =>
                  handleGoogleCompleteInputChange(
                    "mobileNumber",
                    e.target.value,
                  )
                }
                placeholder="Enter your mobile number"
                icon={Phone}
              />
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showGooglePassword ? "text" : "password"}
                    value={googleCompleteData.password}
                    onChange={(e) =>
                      handleGoogleCompleteInputChange(
                        "password",
                        e.target.value,
                      )
                    }
                    className="w-full pl-4 pr-12 py-3 bg-white/70 border border-slate-200/60 rounded-xl"
                    placeholder="Create a password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowGooglePassword(!showGooglePassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                  >
                    {showGooglePassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type={showGoogleConfirmPassword ? "text" : "password"}
                    value={googleCompleteData.confirmPassword}
                    onChange={(e) =>
                      handleGoogleCompleteInputChange(
                        "confirmPassword",
                        e.target.value,
                      )
                    }
                    className="w-full pl-4 pr-12 py-3 bg-white/70 border border-slate-200/60 rounded-xl"
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setShowGoogleConfirmPassword(!showGoogleConfirmPassword)
                    }
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                  >
                    {showGoogleConfirmPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className={`w-full bg-gradient-to-r from-indigo-500 to-blue-600 text-white py-3 rounded-xl font-medium flex items-center justify-center ${loading ? "opacity-70" : ""}`}
              >
                {loading ? "Finishing up..." : "Complete Sign Up"}
                <ArrowRight className="ml-2 w-5 h-5" />
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
